import { FunctionComponent, ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";
import { User } from "@/pages/Public/pages/Login/schema/login";
import { GenerationPhase } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

import { LanguageSelectDropdown } from "./LanguageSelectDropdown";
import { SenderSelect } from "./SenderSelect";
import { SenderOption } from "./senderOptions";
import { Channel, ChannelSelect } from "./ChannelSelect";

interface GenerateBarControlRowProps {
	/** DetailView "Chat" tab / extension panel: one packed line, icon-only controls. */
	compact?: boolean;
	/** Render a smaller, short-label ("Generate", XS) button — the reach-out menu wants a compact CTA
	 * even in the roomier non-compact layout. */
	smallGenerate?: boolean;
	isGenerating: boolean;
	alreadyInProgress: boolean;
	/** Queued or running: the button says which instead of "Please wait" (PREC-4052). */
	generationPhase?: GenerationPhase | null;
	prospectNotResolved?: boolean;
	// Language
	language: string;
	onSelectLanguage: (language: string) => void;
	onOpenRules: () => void;
	// Sender
	/** Every colleague the sender picker lists for this lead (see `buildSenderOptions`). */
	senderOptions: SenderOption[];
	selectedSendingUser: User | null;
	setSelectedSendingUser: (user: User | null) => void;
	connectedUserIds?: Set<number>;
	/** Senders whose own invitation to the lead is pending (PREC-4216). */
	pendingInvitationUserIds?: Set<number>;
	connectionCheckDone?: boolean;
	prospectFirstName?: string | null;
	linkedinInviteNote: boolean;
	// Channel
	availableChannels: Channel[];
	selectedChannel: Channel | null;
	setSelectedChannel: (channel: Channel) => void;
	portalContainer?: HTMLElement | null;
	onGenerate: () => void;
}

/**
 * Row 2 of the GenerateBar (single mode): the language, sender and channel
 * dropdowns plus the "Generate" button. PREC-3570 moved the language control out
 * of the instruction row into this row, left of the sender, so the card reads as
 * two aligned rows (textarea, then controls). `compact` (the DetailView "Chat"
 * tab / extension panel) packs everything onto one line with icon-only controls
 * and a full-width Generate button.
 */
export const GenerateBarControlRow: FunctionComponent<GenerateBarControlRowProps> = ({
	compact,
	smallGenerate,
	isGenerating,
	alreadyInProgress,
	generationPhase = null,
	prospectNotResolved,
	language,
	onSelectLanguage,
	onOpenRules,
	senderOptions,
	selectedSendingUser,
	setSelectedSendingUser,
	connectedUserIds,
	pendingInvitationUserIds,
	connectionCheckDone,
	prospectFirstName,
	linkedinInviteNote,
	availableChannels,
	selectedChannel,
	setSelectedChannel,
	portalContainer,
	onGenerate,
}) => {
	const controls = (
		<>
			<LanguageSelectDropdown
				compact={compact}
				disabled={isGenerating}
				language={language}
				portalContainer={portalContainer}
				onOpenRules={onOpenRules}
				onSelect={onSelectLanguage}
			/>
			<SenderSelect
				compact={compact}
				connectedUserIds={connectedUserIds}
				connectionCheckDone={connectionCheckDone}
				options={senderOptions}
				pendingInvitationUserIds={pendingInvitationUserIds}
				portalContainer={portalContainer}
				prospectFirstName={prospectFirstName}
				selectedSendingUser={selectedSendingUser}
				setSelectedSendingUser={setSelectedSendingUser}
				showLinkedinTier={selectedChannel === "linkedin" && linkedinInviteNote}
			/>
			<ChannelSelect
				availableChannels={availableChannels}
				compact={compact}
				portalContainer={portalContainer}
				selectedChannel={selectedChannel}
				setSelectedChannel={setSelectedChannel}
			/>
		</>
	);

	const { t } = useTranslation();
	const phase = isGenerating ? generationPhase : null;
	const phaseImage: Record<GenerationPhase, ReactElement> = {
		queued: <span className="mr-2 h-2 w-2 animate-pulse rounded-full bg-white" />,
		running: (
			<span className="mr-2 flex">
				<Spinner />
			</span>
		),
	};
	const phaseTitle: Record<GenerationPhase, string> = {
		queued: t("messages.generateBar.status.buttonQueued"),
		running: t("messages.generateBar.status.generating"),
	};
	const idleTitle = compact || smallGenerate ? "Generate" : "Generate 3 draft messages";

	const generateButton = (
		<Button
			className={compact ? "flex-1 !w-full" : undefined}
			color={ButtonColor.ACTION}
			disabled={isGenerating || alreadyInProgress || !selectedSendingUser || prospectNotResolved}
			image={phase ? phaseImage[phase] : <Icon className="mr-1" icon={faSparkles} />}
			isLoading={!phase && (isGenerating || alreadyInProgress)}
			size={smallGenerate ? ButtonSize.XS : ButtonSize.S}
			testId="generate-drafts-button"
			title={phase ? phaseTitle[phase] : idleTitle}
			onClick={onGenerate}
		/>
	);

	if (compact) {
		return (
			<div className="flex items-center gap-2">
				{controls}
				{generateButton}
			</div>
		);
	}

	return (
		<div className="flex flex-wrap items-center justify-between gap-2 gap-y-2">
			<div className="flex w-fit items-center gap-2">{controls}</div>
			<div className="w-fit">{generateButton}</div>
		</div>
	);
};
